/**
 * Dev-only export panel.
 *
 * Format choice: Markdown (.md) or JSON (.json). Both contain the same
 * data — pick by what you'll do with it (read it / feed to an AI assistant
 * / parse with a script).
 *
 * "Include screenshots" wraps the text file in a zip alongside an
 * `images/` folder. Each report gets a sequential reference number
 * (RPT-0001, RPT-0002, …) and screenshots are named
 * `images/RPT-NNNN-MM.{ext}`. The text file references them by relative
 * path so they resolve when the zip is extracted into a folder.
 *
 * Filter buttons trigger the export with the chosen format + checkbox.
 *
 * On a successful export, calls markBulkExported with `Date.now()` so
 * the "since last" bookmark advances on the next run.
 */

import React from 'react';
import {
  useLastBulkExportAt,
  useMarkBulkExported,
} from '../hooks/useBugReports.js';
import { exportBugReports } from '../claudeExport.js';

const FORMATS = [
  { id: 'md',   label: 'Markdown',  hint: '.md — readable text, paste into an AI assistant or open in any editor' },
  { id: 'json', label: 'JSON',      hint: '.json — structured for scripts and programmatic processing' },
];

export default function ExportPanel() {
  const lastExportAt = useLastBulkExportAt();
  const markBulkExported = useMarkBulkExported();
  const [busy, setBusy] = React.useState(false);
  const [format, setFormat] = React.useState('md');
  const [includeScreenshots, setIncludeScreenshots] = React.useState(true);
  const [message, setMessage] = React.useState(null);
  const [error, setError] = React.useState(null);

  const run = async (mode) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const startTs = Date.now();
      const result = await exportBugReports({
        mode,
        since: lastExportAt || 0,
        format,
        includeScreenshots,
      });
      if (result.count === 0) {
        setMessage('No reports matched the filter — nothing to export.');
      } else {
        setMessage(`Exported ${result.count} report${result.count === 1 ? '' : 's'} → ${result.filename}`);
        await markBulkExported({ ts: startTs });
      }
    } catch (e) {
      console.error('Export failed:', e);
      setError(e?.message || 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  const lastExportLabel =
    lastExportAt && lastExportAt > 0
      ? `Last exported: ${new Date(lastExportAt).toLocaleString()}`
      : 'No prior exports yet.';

  const activeFmt = FORMATS.find(f => f.id === format) || FORMATS[0];

  // Friendly preview of the resulting filename + structure.
  const filenamePreview = includeScreenshots
    ? `bug-reports-beta.zip → bug-reports-beta.${format} + images/RPT-NNNN-MM.png`
    : `bug-reports-beta.${format}`;

  return (
    <div>
      <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 4 }}>
        📦 Export reports
      </div>
      <div style={{ fontSize: '0.62rem', color: '#888', marginBottom: 10 }}>
        {lastExportLabel}
      </div>

      {/* Format selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
        {FORMATS.map(f => {
          const selected = format === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFormat(f.id)}
              disabled={busy}
              style={{
                padding: '4px 10px',
                fontSize: '0.7rem',
                background: selected ? 'rgba(201,162,39,0.18)' : 'transparent',
                border: `1px solid ${selected ? '#c9a227' : 'rgba(255,255,255,0.12)'}`,
                borderRadius: 4,
                color: selected ? '#c9a227' : '#888',
                cursor: busy ? 'wait' : 'pointer',
                fontWeight: selected ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 10, fontStyle: 'italic' }}>
        {activeFmt.hint}
      </div>

      {/* Include screenshots toggle */}
      <label style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 10px',
        background: 'rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 4,
        marginBottom: 10,
        cursor: busy ? 'wait' : 'pointer',
      }}>
        <input
          type="checkbox"
          checked={includeScreenshots}
          onChange={e => setIncludeScreenshots(e.target.checked)}
          disabled={busy}
          style={{ accentColor: '#c9a227' }}
        />
        <span style={{ fontSize: '0.72rem', color: '#e8d5b7' }}>
          Include screenshots (zip with <code style={{ color: '#c9a227' }}>images/</code> folder)
        </span>
      </label>
      <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 10, fontFamily: 'monospace' }}>
        → {filenamePreview}
      </div>

      {/* Filter buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button
          onClick={() => run('all')}
          disabled={busy}
          style={{
            padding: '5px 10px',
            fontSize: '0.72rem',
            background: 'rgba(74,144,217,0.12)',
            border: '1px solid rgba(74,144,217,0.4)',
            borderRadius: 5,
            color: '#4a90d9',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          All reports
        </button>
        <button
          onClick={() => run('open')}
          disabled={busy}
          style={{
            padding: '5px 10px',
            fontSize: '0.72rem',
            background: 'rgba(255,105,97,0.10)',
            border: '1px solid rgba(255,105,97,0.4)',
            borderRadius: 5,
            color: '#ff6961',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Open only
        </button>
        <button
          onClick={() => run('since')}
          disabled={busy}
          style={{
            padding: '5px 10px',
            fontSize: '0.72rem',
            background: 'rgba(39,201,90,0.10)',
            border: '1px solid rgba(39,201,90,0.4)',
            borderRadius: 5,
            color: '#27c95a',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Since last export
        </button>
      </div>

      {message && (
        <div style={{ fontSize: '0.7rem', color: '#27c95a', marginTop: 8 }}>{message}</div>
      )}
      {error && (
        <div style={{ fontSize: '0.7rem', color: '#ff6961', marginTop: 8 }}>{error}</div>
      )}
    </div>
  );
}
